import { motion } from "framer-motion";
import { CloudCog, Workflow, ShieldCheck, ScanSearch } from "lucide-react";

const SERVICES = [
  {
    icon: CloudCog,
    num: "01",
    title: "Cloud Infrastructure & DevOps",
    copy: "Landing zones, Kubernetes platforms and CI/CD that deploys dozens of times a day without drama.",
    points: ["IaC & platform engineering", "SRE & cost governance", "24/7 managed cloud"],
    testid: "service-card-cloud",
  },
  {
    icon: Workflow,
    num: "02",
    title: "Modernization & Microservices",
    copy: "We decompose monoliths into services that deploy independently, scale honestly and fail safely.",
    points: ["Domain-driven architecture", "Strangler-fig migrations", "API & integration layers"],
    testid: "service-card-modernization",
  },
  {
    icon: ShieldCheck,
    num: "03",
    title: "Cyber Resilience & Compliance",
    copy: "Security engineered into the delivery pipeline — audited, attested and rehearsed against failure.",
    points: ["Security hardening & audits", "ISO 27001 / SOC 2 readiness", "DR & incident playbooks"],
    testid: "service-card-cyber",
  },
  {
    icon: ScanSearch,
    num: "04",
    title: "AI & Automated Testing",
    copy: "Intelligent QA that keeps pace with shipping — from generated test suites to AI-assisted triage.",
    points: ["AI-assisted test generation", "Continuous regression gates", "Quality analytics"],
    testid: "service-card-ai-testing",
  },
];

const EASE = [0.16, 1, 0.3, 1];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="scroll-mt-24 bg-paper py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-14">
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mb-5 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.28em] text-brand"
          >
            <span className="h-px w-10 bg-brand" />
            Consulting Practice
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.05 }}
            className="max-w-2xl font-heading text-3xl font-extrabold tracking-tight text-ink sm:text-4xl lg:text-[2.75rem] lg:leading-[1.12]"
          >
            Four disciplines. Zero hand-offs lost.
          </motion.h2>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.testid}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.7, ease: EASE, delay: 0.07 * i }}
              data-testid={s.testid}
              className="group flex h-full flex-col rounded-3xl border border-slate-200/80 bg-white p-8 transition-all duration-200 hover:-translate-y-1 hover:border-brand/40 hover:shadow-[0_24px_56px_rgba(0,82,255,0.1)]"
            >
              <div className="flex items-center justify-between">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-50 text-ink transition-colors duration-200 group-hover:bg-brand group-hover:text-white">
                  <s.icon className="h-5 w-5" />
                </span>
                <span className="font-mono text-[10px] tracking-[0.2em] text-slate-300">
                  {s.num}
                </span>
              </div>
              <h3 className="mt-6 font-heading text-xl font-bold tracking-tight text-ink">
                {s.title}
              </h3>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">
                {s.copy}
              </p>
              <ul className="mt-6 space-y-2 border-t border-slate-100 pt-5">
                {s.points.map((p) => (
                  <li
                    key={p}
                    className="flex items-center gap-2.5 font-mono text-[10px] uppercase tracking-[0.14em] text-slate-500"
                  >
                    <span className="h-1 w-1 rounded-full bg-brand" />
                    {p}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
